<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { useToast } from "primevue/usetoast"
import { storeToRefs } from "pinia"
import { useRouter } from "vue-router"
import { useSourceStore } from "@core/stores/source.store"
import { isSourceSidebarRoute } from "@core/router/restrictedAccessNavigation"
import { redirectOnSourceViewsLinkRevoked } from "@core/router/sourceViewsLinkRevokedRedirect"

// ================================
// PROPS & EMITS
// ================================

const props = defineProps<{
    /** Identifiant Mongo de la source liée à la demande */
    sourceId: string
    /** Libellé affiché dans le message (ex. idsource métier) */
    sourceLabel?: string
}>()

const emit = defineEmits<{
    /** Émis après suppression réussie (API + mise à jour stores via source.store) */
    success: [payload: { sourceId: string }]
}>()

const visible = defineModel<boolean>("visible", { default: false })

// ================================
// COMPOSABLES
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            message:
                "Êtes-vous sûr de vouloir supprimer la demande de consentement pour la source {id} ? Cette action est irréversible.",
            header: "Confirmation de suppression",
            cancel: "Annuler",
            confirm: "Supprimer",
            successSummary: "Suppression réussie",
            successDetail: "La demande de consentement a été supprimée",
            errorSummary: "Erreur",
            errorDetail:
                "Une erreur est survenue lors de la suppression de la demande",
            errorForbiddenDetail:
                "Vous n'avez pas les droits pour supprimer cette demande. Demandez à un administrateur de votre entreprise ou au créateur de la demande.",
        },
    },
})

const sourceStore = useSourceStore()
const toast = useToast()
const router = useRouter()

const {
    isFetchingDeleteConsentRequest: getIsDeleting,
    hasErrorDeleteConsentRequest: getHasError,
    errorDeleteConsentRequest: getDeleteError,
    getSourceById,
    sources,
} = storeToRefs(sourceStore)

// ================================
// COMPUTED
// ================================

const getMessage = computed(() =>
    t("message", {
        id: props.sourceLabel || props.sourceId,
    })
)

// ================================
// HANDLERS
// ================================

function onVisibleUpdate(v: boolean): void {
    if (!v && getIsDeleting.value) return
    visible.value = v
}

function onCancel(): void {
    if (getIsDeleting.value) return
    visible.value = false
}

/**
 * Supprime la demande de consentement (store : API + retrait bâtiment + liste sources).
 */
async function onConfirm(): Promise<void> {
    if (!props.sourceId) return

    await sourceStore.fetchDeleteConsentRequest(props.sourceId)

    // Toujours fermer la modale, succès ou erreur — sinon le user reclique
    // sans comprendre pourquoi "rien ne se passe" (cf. DEV-485).
    visible.value = false

    if (getHasError.value) {
        const err = getDeleteError.value
        const isForbidden = err?.code === "restricted_user_consentrequest"
        toast.add({
            severity: "error",
            summary: t("errorSummary"),
            detail: isForbidden
                ? t("errorForbiddenDetail")
                : (err?.message?.toString() ?? t("errorDetail")),
            life: 5000,
        })
        return
    }

    await sourceStore.fetchSourceById({
        _id_source: props.sourceId,
        silent: true,
    })

    if (
        !sourceStore.hasErrorSourceById &&
        getSourceById.value(props.sourceId)
            ?.company_source_views_link_revoked === true &&
        isSourceSidebarRoute(router.currentRoute.value)
    ) {
        await redirectOnSourceViewsLinkRevoked({
            router,
            toast,
            sources,
            _id_source: props.sourceId,
        })
        return
    }

    toast.add({
        severity: "success",
        summary: t("successSummary"),
        detail: t("successDetail"),
        life: 3000,
    })

    emit("success", { sourceId: props.sourceId })
}
</script>

<template>
    <BaseDialog
        :visible="visible"
        modal
        :header="t('header')"
        :closable="!getIsDeleting"
        :dismissable-mask="!getIsDeleting"
        :close-on-escape="!getIsDeleting"
        class="max-w-md w-full"
        :breakpoints="{ '575px': '90vw' }"
        @update:visible="onVisibleUpdate"
    >
        <div class="flex gap-3 items-start">
            <span
                class="pi pi-info-circle text-primary text-xl shrink-0 mt-0.5"
                aria-hidden="true"
            />
            <p class="text-surface-700 m-0 leading-relaxed">
                {{ getMessage }}
            </p>
        </div>
        <template #footer>
            <div class="flex justify-end gap-2 w-full">
                <BaseButton
                    :label="t('cancel')"
                    severity="secondary"
                    outlined
                    :disabled="getIsDeleting"
                    @click="onCancel"
                />
                <BaseButton
                    :label="t('confirm')"
                    icon="pi pi-trash"
                    severity="danger"
                    :loading="getIsDeleting"
                    :disabled="getIsDeleting"
                    @click="onConfirm"
                />
            </div>
        </template>
    </BaseDialog>
</template>
